/**
 * Property Service for Outlook Add-in
 * Handles property data fetching from DoNexus API
 */

import apiClient from '../utils/apiClient';

class PropertyService {
  constructor() {
    this.propertyId = null; // Property ID will be set dynamically from incident communications
  }

  /**
   * Fetch property data by ID
   * @returns {Promise<Object>} Property data object
   */
  async getProperty() {
    if (!this.propertyId) {
      throw new Error('Property ID not set. Please ensure incident communications data is loaded first.');
    }

    try {
      const response = await apiClient.get(`/property/${this.propertyId}`);
      
      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: response.message
        };
      } else {
        throw new Error(response.message || 'Failed to fetch property data');
      }
    } catch (error) {
      console.error('Error fetching property data:', error);
      throw error;
    }
  }

  /**
   * Get property ID
   * @returns {string} Current property ID
   */
  getPropertyId() {
    return this.propertyId;
  }

  /**
   * Set property ID (for future use when property selection is implemented)
   * @param {string} id - Property ID
   */
  setPropertyId(id) {
    this.propertyId = id;
  }

  /**
   * Get all properties with pagination and filtering
   * @param {Object} filters - Optional filters object
   * @param {Object} options - Optional pagination options
   * @returns {Promise<Object>} Paginated response of properties
   */
  async getAllProperties(filters = {}, options = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Add pagination parameters
      if (options.page) queryParams.append('page', options.page.toString());
      if (options.limit) queryParams.append('limit', options.limit.toString());

      // Add filter parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const queryString = queryParams.toString();
      const endpoint = queryString ? `/property?${queryString}` : '/property';
      
      const response = await apiClient.get(endpoint);
      
      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: response.message
        };
      } else {
        throw new Error(response.message || 'Failed to fetch properties');
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      throw error;
    }
  }
}

// Create singleton instance
const propertyService = new PropertyService();

export default propertyService;
